import { supabase } from "@/integrations/supabase/client";

/**
 * Cuentas de contrapartida para los asientos de pago (SUENLACE) por empresa.
 *
 *  - `cashAccount`   → cuenta de caja para CONTADO/EFECTIVO/CAJA.
 *  - `cardAccounts`  → lista de tarjetas (cada una con etiqueta y cuenta).
 *
 * Si la empresa no tiene cuentas configuradas, no se propone asiento de pago
 * automático (el usuario puede activarlo manualmente factura a factura).
 */
export interface CardAccount {
  label: string;
  account: string;
}

export interface CompanyPaymentAccounts {
  companyId: string;
  cashAccount: string;
  cardAccounts: CardAccount[];
  /** Compatibilidad: primera tarjeta de la lista, o "". */
  cardAccount: string;
}

const EMPTY: Omit<CompanyPaymentAccounts, "companyId"> = {
  cashAccount: "",
  cardAccounts: [],
  cardAccount: "",
};

const normalizeCards = (raw: unknown): CardAccount[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c: any) => ({
      label: String(c?.label ?? "").trim(),
      account: String(c?.account ?? "").trim(),
    }))
    .filter((c) => c.account.length > 0);
};

export const loadCompanyPaymentAccounts = async (
  companyId: string,
): Promise<CompanyPaymentAccounts> => {
  const { data, error } = await supabase
    .from("company_payment_accounts")
    .select("company_id, cash_account, card_account, card_accounts")
    .eq("company_id", companyId)
    .maybeSingle();
  if (error) {
    console.warn("[companyPaymentAccounts] load error:", error);
    return { companyId, ...EMPTY };
  }
  if (!data) return { companyId, ...EMPTY };
  let cards = normalizeCards((data as any).card_accounts);
  // Fallback al campo antiguo si la lista está vacía.
  if (cards.length === 0 && (data.card_account || "").trim()) {
    cards = [{ label: "Tarjeta", account: data.card_account.trim() }];
  }
  return {
    companyId,
    cashAccount: (data.cash_account || "").trim(),
    cardAccounts: cards,
    cardAccount: cards[0]?.account ?? "",
  };
};

export const upsertCompanyPaymentAccounts = async (
  companyId: string,
  cashAccount: string,
  cardAccounts: CardAccount[],
): Promise<{ error?: string }> => {
  const cleanCards = cardAccounts
    .map((c) => ({ label: (c.label || "").trim(), account: (c.account || "").trim() }))
    .filter((c) => c.account.length > 0);
  const payload = {
    company_id: companyId,
    cash_account: cashAccount.trim(),
    card_account: cleanCards[0]?.account ?? "", // compat
    card_accounts: cleanCards as any,
  };
  const { error } = await supabase
    .from("company_payment_accounts")
    .upsert(payload, { onConflict: "company_id" });
  return { error: error?.message };
};

/**
 * Dada una forma de pago (texto libre que llega del nombre de fichero o que el
 * usuario ha editado) y las cuentas configuradas de la empresa, devuelve la
 * cuenta de contrapartida sugerida y un identificador de tipo ("cash" | "card").
 * Si hay varias tarjetas configuradas, intenta emparejar por dígitos
 * (p. ej. paymentMethod "TJ6029" → tarjeta con label/cuenta que contenga "6029").
 * Si no encuentra coincidencia, devuelve la primera tarjeta como sugerencia.
 */
export type PaymentEntryKind = "cash" | "card";

export const inferPaymentEntryAccount = (
  paymentMethod: string | null | undefined,
  accounts:
    | { cashAccount: string; cardAccounts?: CardAccount[]; cardAccount?: string }
    | null
    | undefined,
): { kind: PaymentEntryKind; account: string } | null => {
  if (!accounts) return null;
  const pm = (paymentMethod || "").toUpperCase();
  if (!pm) return null;
  // Tarjeta
  if (/(^|[^A-Z])(TARJETA|TJ|CTJ)\d*/.test(pm)) {
    const cards =
      accounts.cardAccounts && accounts.cardAccounts.length > 0
        ? accounts.cardAccounts
        : accounts.cardAccount
        ? [{ label: "Tarjeta", account: accounts.cardAccount }]
        : [];
    if (cards.length === 0) return null;
    // Buscar coincidencia por dígitos del método de pago.
    const digits = (pm.match(/\d{3,}/g) || []);
    for (const d of digits) {
      const m = cards.find(
        (c) => c.label.includes(d) || c.account.includes(d),
      );
      if (m) return { kind: "card", account: m.account };
    }
    return { kind: "card", account: cards[0].account };
  }
  // Contado / efectivo / caja
  if (/(^|[^A-Z])(CONTADO|EFECTIVO|CAJA)([^A-Z]|$)/.test(pm)) {
    if (accounts.cashAccount) return { kind: "cash", account: accounts.cashAccount };
    return null;
  }
  return null;
};
